import { defineStore } from 'pinia'

import type { DeckCardInput } from '../../deck/types'
import type { DeckElementInteraction } from '../../deck/types'
import { resolvePrimaryCheckbox } from '../helpers/primaryCheckbox'
import { isDone } from '../helpers/applyCheckboxState'

export type DeckPlayerCardState = {
  cardId: string
  primaryValues: boolean[]
  isDone: boolean
}

export type DeckPlayerSession = {
  deckId: string
  cardIds: string[] // all card ids for the deck, in original order
  queue: string[] // active is queue[0]
  remaining: string[] // ids not currently in the pool
  total: number
  valuesByCard: Record<string, boolean[]>
  doneIds: Record<string, true>

  poolSize: number // fixed default (currently 10)

  statsByCard: Record<string, { correctCount: number; wrongCount: number }>

  // action history for undo/redo
  undo: DeckPlayerAction[]
  redo: DeckPlayerAction[]
}

export type DeckPlayerAction =
  | {
      type: 'checkbox-toggle'
      cardId: string
      prevValues: boolean[]
      nextValues: boolean[]
      prevQueue: string[]
      nextQueue: string[]
      prevRemaining: string[]
      nextRemaining: string[]
      prevDone: Record<string, true>
      nextDone: Record<string, true>
      prevStats: { correctCount: number; wrongCount: number }
      nextStats: { correctCount: number; wrongCount: number }
    }
  | {
      type: 'reset'
      prev: Omit<DeckPlayerSession, 'undo' | 'redo'>
      next: Omit<DeckPlayerSession, 'undo' | 'redo'>
    }
  | {
      type: 'nav'
      label: 'next' | 'back' | 'skip'
      prevQueue: string[]
      nextQueue: string[]
      prevRemaining: string[]
      nextRemaining: string[]
    }

const cloneDone = (input: Record<string, true>) => ({ ...input })

const shuffleInPlace = (ids: string[]) => {
  // Fisher–Yates
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const tmp = ids[i]
    ids[i] = ids[j]!
    ids[j] = tmp!
  }
}

const pickWeightedIndex = (weights: number[]) => {
  const total = weights.reduce((sum, w) => sum + (Number.isFinite(w) && w > 0 ? w : 0), 0)
  if (total <= 0) return -1
  let r = Math.random() * total
  for (let i = 0; i < weights.length; i++) {
    const w = Number.isFinite(weights[i]) && weights[i]! > 0 ? weights[i]! : 0
    r -= w
    if (r <= 0) return i
  }
  return weights.length - 1
}

const selectNextWeightedInPool = (opts: {
  queue: string[]
  statsByCard: Record<string, { correctCount: number; wrongCount: number }>
  avoidId?: string | null
}) => {
  const queue = opts.queue
  if (queue.length <= 1) return queue.slice()
  const active = queue[0]!

  const candidates = queue.slice(1)
  const filtered = opts.avoidId ? candidates.filter((id) => id !== opts.avoidId) : candidates
  const pool = filtered.length > 0 ? filtered : candidates
  if (pool.length === 0) return queue.slice()

  const weights = pool.map((id) => {
    const stats = opts.statsByCard[id]
    const wrong = typeof stats?.wrongCount === 'number' ? stats.wrongCount : 0
    // Simple bias: higher wrongCount => more likely.
    return 1 + Math.max(0, wrong)
  })

  const idx = pickWeightedIndex(weights)
  const chosen = idx >= 0 ? pool[idx]! : pool[Math.floor(Math.random() * pool.length)]!
  const rest = queue.filter((id) => id !== chosen)
  return [chosen, ...rest]
}

const poolRefill = (queue: string[], remaining: string[], desiredPoolSize: number) => {
  if (queue.length >= desiredPoolSize) return { queue: queue.slice(), remaining: remaining.slice() }

  const nextQueue = queue.slice()
  const nextRemaining = remaining.slice()
  while (nextQueue.length < desiredPoolSize && nextRemaining.length > 0) {
    const id = nextRemaining.shift()!
    // Avoid duplicates, but be defensive.
    if (!nextQueue.includes(id)) nextQueue.push(id)
  }
  return { queue: nextQueue, remaining: nextRemaining }
}

export const useDeckPlayerStore = defineStore('deckplayer', {
  state: () => ({
    sessions: {} as Record<string, DeckPlayerSession>,
    lastAdvanceOutcome: {} as Record<string, 'success' | 'failed' | null>,
  }),
  actions: {
    initSession(
      deckId: string,
      cards: DeckCardInput[],
      opts?: {
        initialValues?: Record<string, boolean[]>
        /** 'flowchart': sequential order, all cards in queue, no weighted shuffle. */
        mode?: 'checkbox' | 'flowchart'
      },
    ) {
      const uniqueId = deckId || 'default'
      const isFlowchart = opts?.mode === 'flowchart'

      const cardIds = (cards ?? []).map((c) => c.id).filter(Boolean)
      const total = cardIds.length

      const poolSize = isFlowchart ? total : 10

      let base = cardIds.slice()
      // In checkbox mode shuffle for variety; flowchart mode preserves group order.
      if (!isFlowchart) shuffleInPlace(base)

      const valuesByCard: Record<string, boolean[]> = {}
      const doneIds: Record<string, true> = {}
      const statsByCard: Record<string, { correctCount: number; wrongCount: number }> = {}

      for (const card of cards) {
        const initial = opts?.initialValues?.[card.id]
        if (Array.isArray(initial)) {
          valuesByCard[card.id] = initial.slice()
          if (isDone(initial)) doneIds[card.id] = true
        }

        statsByCard[card.id] = { correctCount: 0, wrongCount: 0 }
      }

      // remove already-done
      base = base.filter((id) => !doneIds[id])

      const queue = base.slice(0, poolSize)
      const remaining = base.slice(poolSize)

      this.sessions[uniqueId] = {
        deckId: uniqueId,
        cardIds: cardIds.slice(),
        queue,
        remaining,
        total,
        valuesByCard,
        doneIds,
        poolSize,
        statsByCard,
        undo: [],
        redo: [],
      }

      this.lastAdvanceOutcome[uniqueId] = null
    },

    getSession(deckId: string) {
      return this.sessions[deckId || 'default']
    },

    reset(deckId: string, cards?: DeckCardInput[]) {
      const id = deckId || 'default'
      const existing = this.sessions[id]
      if (!existing) {
        this.initSession(id, cards ?? [], {})
        return
      }

      this.lastAdvanceOutcome[id] = null

      const prev: Omit<DeckPlayerSession, 'undo' | 'redo'> = {
        deckId: existing.deckId,
        cardIds: existing.cardIds.slice(),
        queue: existing.queue.slice(),
        remaining: existing.remaining.slice(),
        total: existing.total,
        valuesByCard: { ...existing.valuesByCard },
        doneIds: cloneDone(existing.doneIds),
        poolSize: existing.poolSize,
        statsByCard: { ...existing.statsByCard },
      }

      if (Array.isArray(cards)) {
        this.initSession(id, cards, {})
      } else {
        // Reset from session snapshot (supports widget-only controls).
        const poolSize = 10
        let base = existing.cardIds.slice()
        shuffleInPlace(base)

        const queue = base.slice(0, poolSize)
        const remaining = base.slice(poolSize)

        const statsByCard: Record<string, { correctCount: number; wrongCount: number }> = {}
        for (const id of existing.cardIds) {
          if (!id) continue
          statsByCard[id] = { correctCount: 0, wrongCount: 0 }
        }

        this.sessions[id] = {
          deckId: existing.deckId,
          cardIds: existing.cardIds.slice(),
          queue,
          remaining,
          total: existing.total,
          valuesByCard: {},
          doneIds: {},
          poolSize,
          statsByCard,
          undo: [],
          redo: [],
        }
      }

      const nextSession = this.sessions[id]!
      const next: Omit<DeckPlayerSession, 'undo' | 'redo'> = {
        deckId: nextSession.deckId,
        cardIds: nextSession.cardIds.slice(),
        queue: nextSession.queue.slice(),
        remaining: nextSession.remaining.slice(),
        total: nextSession.total,
        valuesByCard: { ...nextSession.valuesByCard },
        doneIds: cloneDone(nextSession.doneIds),
        poolSize: nextSession.poolSize,
        statsByCard: { ...nextSession.statsByCard },
      }

      // preserve history stack with an action
      nextSession.undo = [...existing.undo, { type: 'reset', prev, next }]
      nextSession.redo = []

      this.lastAdvanceOutcome[id] = null
    },

    next(deckId: string, label: 'next' | 'skip' = 'next') {
      const session = this.getSession(deckId)
      if (!session) return
      if (session.queue.length <= 1) return

      this.lastAdvanceOutcome[deckId || 'default'] = null

      const prevQueue = session.queue.slice()
      const prevRemaining = session.remaining.slice()

      session.queue = selectNextWeightedInPool({
        queue: session.queue,
        statsByCard: session.statsByCard,
        avoidId: null,
      })

      const nextQueue = session.queue.slice()
      const nextRemaining = session.remaining.slice()

      session.undo.push({
        type: 'nav',
        label,
        prevQueue,
        nextQueue,
        prevRemaining,
        nextRemaining,
      })
      session.redo = []
    },

    back(deckId: string) {
      const session = this.getSession(deckId)
      if (!session) return
      if (session.queue.length <= 1) return

      this.lastAdvanceOutcome[deckId || 'default'] = null

      const prevQueue = session.queue.slice()
      const prevRemaining = session.remaining.slice()

      // "Back" navigates within the pool by swapping the first two items.
      if (session.queue.length >= 2) {
        const [a, b, ...rest] = session.queue
        session.queue = [b!, a!, ...rest]
      }

      const nextQueue = session.queue.slice()
      const nextRemaining = session.remaining.slice()

      session.undo.push({
        type: 'nav',
        label: 'back',
        prevQueue,
        nextQueue,
        prevRemaining,
        nextRemaining,
      })
      session.redo = []
    },

    handleElementInteraction(
      deckId: string,
      cards: DeckCardInput[],
      interaction: DeckElementInteraction,
    ) {
      const session = this.getSession(deckId)
      if (!session) return { advanced: false, removedCardId: null as string | null }

      const activeId = session.queue[0]
      if (!activeId) return { advanced: false, removedCardId: null as string | null }

      if (interaction.elementType !== 'checkbox-group')
        return { advanced: false, removedCardId: null as string | null }

      const activeCard = cards.find((c) => c.id === activeId)
      if (!activeCard) return { advanced: false, removedCardId: null as string | null }

      const primary = resolvePrimaryCheckbox(activeCard.frontElements, activeCard.backElements)
      if (!primary) return { advanced: false, removedCardId: null as string | null }

      // Only primary checkbox-group triggers behavior.
      if (interaction.face !== primary.face || interaction.elementIndex !== primary.elementIndex) {
        return { advanced: false, removedCardId: null as string | null }
      }

      const detail = interaction.detail as { values?: unknown }
      const nextValues = Array.isArray(detail?.values) ? detail.values.map(Boolean) : []
      const prevValues = Array.isArray(session.valuesByCard[activeId])
        ? session.valuesByCard[activeId]!.slice()
        : []

      // Success/fail is based on the user's primary checkbox toggle direction:
      // - unchecked -> checked = success
      // - checked -> unchecked = failed
      // This is intentionally independent of `isDone()` (which may require multiple checks).
      const findToggleOutcome = (prev: boolean[], next: boolean[]): 'success' | 'failed' | null => {
        const n = Math.max(prev.length, next.length)
        for (let i = 0; i < n; i++) {
          const a = Boolean(prev[i])
          const b = Boolean(next[i])
          if (a === b) continue
          return b ? 'success' : 'failed'
        }
        return null
      }
      const toggleOutcome = findToggleOutcome(prevValues, nextValues)

      const prevQueue = session.queue.slice()
      const prevRemaining = session.remaining.slice()
      const prevDone = cloneDone(session.doneIds)

      const prevStats = {
        ...(session.statsByCard[activeId] ?? { correctCount: 0, wrongCount: 0 }),
      }

      // update values
      session.valuesByCard = { ...session.valuesByCard, [activeId]: nextValues.slice() }

      const wasDone = isDone(prevValues)
      const nowDone = isDone(nextValues)

      this.lastAdvanceOutcome[deckId || 'default'] = toggleOutcome

      // update stats (in-memory only; future backend can persist)
      const existingStats = session.statsByCard[activeId] ?? { correctCount: 0, wrongCount: 0 }
      const nextStatsForCard = { ...existingStats }
      if (toggleOutcome === 'success') nextStatsForCard.correctCount += 1
      else if (toggleOutcome === 'failed') nextStatsForCard.wrongCount += 1
      session.statsByCard = { ...session.statsByCard, [activeId]: nextStatsForCard }

      let removedCardId: string | null = null

      if (nowDone) {
        session.doneIds = { ...session.doneIds, [activeId]: true }
        // remove
        session.queue = session.queue.filter((id) => id !== activeId)
        removedCardId = activeId

        const refilled = poolRefill(session.queue, session.remaining, session.poolSize)
        session.queue = refilled.queue
        session.remaining = refilled.remaining

        if (session.queue.length > 1) {
          session.queue = selectNextWeightedInPool({
            queue: session.queue,
            statsByCard: session.statsByCard,
            avoidId: null,
          })
        }
      } else {
        // advance immediately, but keep card in pool
        session.queue = selectNextWeightedInPool({
          queue: session.queue,
          statsByCard: session.statsByCard,
          avoidId: null,
        })
      }

      const nextQueue = session.queue.slice()
      const nextRemaining = session.remaining.slice()
      const nextDone = cloneDone(session.doneIds)

      const nextStats = {
        ...(session.statsByCard[activeId] ?? { correctCount: 0, wrongCount: 0 }),
      }

      session.undo.push({
        type: 'checkbox-toggle',
        cardId: activeId,
        prevValues,
        nextValues: nextValues.slice(),
        prevQueue,
        nextQueue,
        prevRemaining,
        nextRemaining,
        prevDone,
        nextDone,
        prevStats,
        nextStats,
      })
      session.redo = []

      return { advanced: true, removedCardId }
    },

    /**
     * Flowchart mode: mark the active card as done and advance to the next in sequence.
     * Does NOT require any checkbox interaction.
     */
    flowAdvance(deckId: string) {
      const session = this.getSession(deckId)
      if (!session) return { removedCardId: null as string | null }

      const activeId = session.queue[0]
      if (!activeId) return { removedCardId: null as string | null }

      this.lastAdvanceOutcome[deckId || 'default'] = 'success'

      session.doneIds = { ...session.doneIds, [activeId]: true }
      session.queue = session.queue.slice(1)

      return { removedCardId: activeId }
    },

    undo(deckId: string) {
      const session = this.getSession(deckId)
      if (!session) return
      const action = session.undo.pop()
      if (!action) return

      this.lastAdvanceOutcome[deckId || 'default'] = null

      if (action.type === 'checkbox-toggle') {
        session.valuesByCard = {
          ...session.valuesByCard,
          [action.cardId]: action.prevValues.slice(),
        }
        session.queue = action.prevQueue.slice()
        session.remaining = action.prevRemaining.slice()
        session.doneIds = cloneDone(action.prevDone)
        session.statsByCard = {
          ...session.statsByCard,
          [action.cardId]: { ...action.prevStats },
        }
      } else if (action.type === 'nav') {
        session.queue = action.prevQueue.slice()
        session.remaining = action.prevRemaining.slice()
      } else if (action.type === 'reset') {
        session.cardIds = action.prev.cardIds.slice()
        session.queue = action.prev.queue.slice()
        session.remaining = action.prev.remaining.slice()
        session.total = action.prev.total
        session.valuesByCard = { ...action.prev.valuesByCard }
        session.doneIds = cloneDone(action.prev.doneIds)
        session.poolSize = action.prev.poolSize
        session.statsByCard = { ...action.prev.statsByCard }
      }

      session.redo.push(action)
    },

    redo(deckId: string) {
      const session = this.getSession(deckId)
      if (!session) return
      const action = session.redo.pop()
      if (!action) return

      this.lastAdvanceOutcome[deckId || 'default'] = null

      if (action.type === 'checkbox-toggle') {
        session.valuesByCard = {
          ...session.valuesByCard,
          [action.cardId]: action.nextValues.slice(),
        }
        session.queue = action.nextQueue.slice()
        session.remaining = action.nextRemaining.slice()
        session.doneIds = cloneDone(action.nextDone)
        session.statsByCard = {
          ...session.statsByCard,
          [action.cardId]: { ...action.nextStats },
        }
      } else if (action.type === 'nav') {
        session.queue = action.nextQueue.slice()
        session.remaining = action.nextRemaining.slice()
      } else if (action.type === 'reset') {
        session.cardIds = action.next.cardIds.slice()
        session.queue = action.next.queue.slice()
        session.remaining = action.next.remaining.slice()
        session.total = action.next.total
        session.valuesByCard = { ...action.next.valuesByCard }
        session.doneIds = cloneDone(action.next.doneIds)
        session.poolSize = action.next.poolSize
        session.statsByCard = { ...action.next.statsByCard }
      }

      session.undo.push(action)
    },
  },
})
